import { useQueryClient } from '@tanstack/react-query';
import { Layout, message } from 'antd';
import { useContext, useEffect, useLayoutEffect, useState } from 'react';

import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useUpdateHotelContacts } from 'app/hooks/data/core/extranet/hotel/useUpdateHotelContacts';
import {
  hotelPath,
  useGetHotel,
  useUpdateHotel,
} from 'app/hooks/data/hotels/useHotel';
import { useSaveHotelExperience } from 'app/hooks/data/hotels/useHotelExperience';
import { useSaveHotelServices } from 'app/hooks/data/hotels/useHotelServices';
import { useHotelPictures } from 'app/hooks/data/pictures/useHotelPictures';
import { useSaveGallery } from 'app/hooks/data/pictures/useSaveGallery';
import { useDeleteRoom } from 'app/hooks/data/rooms/useRoom';
import { useHotelSpacesTypes } from 'app/hooks/data/useHotelSpacesTypes';
import { useRooms, useSaveBuilderRooms } from 'app/hooks/data/useRooms';
import type { Hotel } from 'app/typings/builder/hotel';
import { ChannelManager } from 'app/typings/channelManagers';
import type { ExtranetHotelContacts } from 'app/typings/core/extranet/hotel/contacts';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { ExperiencesForm } from './ExperiencesForm/ExperiencesForm';
import { FinancialForm } from './FinancialForm/FinancialForm';
import { HeaderContext } from './HeaderContext/HeaderContext';
import './HotelBuilder.scss';
import { MediaForm } from './MediaForm/MediaForm';
import { ProfileForm } from './ProfileForm/ProfileForm';
import { RoomsForm } from './RoomsForm/RoomsForm';
import { ServicesForm } from './ServicesForm/ServicesForm';
import { SideMenu } from './SideMenu/SideMenu';
import { SpacesForm } from './SpacesForm/SpacesForm';
import { ThankYou } from './ThankYou/ThankYou';
import { Welcome } from './Welcome/Welcome';

type StepNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

type Props = {
  location: {
    search: string;
  };
  match: {
    params: {
      hotelId: string;
    };
  };
};

const steps = [
  {
    key: 'profile',
    title: 'Profile',
    order: 1,
  },
  {
    key: 'services',
    title: 'Services',
    order: 2,
  },
  {
    key: 'media',
    title: 'Media',
    order: 3,
  },
  {
    key: 'spaces',
    title: 'Spaces',
    order: 4,
  },
  {
    key: 'rooms',
    title: 'Rooms',
    order: 5,
  },
  {
    key: 'experiences',
    title: 'Activities',
    order: 6,
  },
  {
    key: 'financial_details',
    title: 'Financial details',
    order: 7,
  },
];

export const HotelBuilder = ({ location, match }: Props) => {
  const hotelId = parseInt(match.params.hotelId, 10);

  const queryClient = useQueryClient();
  const { data: hotel } = useGetHotel(hotelId);
  const { mutateAsync: saveHotel, isLoading: isSavingHotel } =
    useUpdateHotel(hotelId);
  const { mutateAsync: saveContacts, isLoading: isSavingContacts } =
    useUpdateHotelContacts(hotelId);

  const { data: pictures = [] } = useHotelPictures(hotelId);
  const { data: spaceTypes = [] } = useHotelSpacesTypes();
  const { mutate: saveServices } = useSaveHotelServices(hotelId.toString(), {
    showSuccessMessage: false,
  });
  const { mutate: saveGallery } = useSaveGallery();
  const { mutate: saveHotelExperience } = useSaveHotelExperience();
  const { isAdmin } = useContext(UserContext);

  const { data: rooms } = useRooms(hotelId);
  const { mutate: saveHotelRooms, isLoading: isSavingRooms } =
    useSaveBuilderRooms(hotelId);

  const { mutateAsync: deleteRoom } = useDeleteRoom(hotelId);

  const [step, setStep] = useState<StepNumber>(0);
  const [validSteps, setValidSteps] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const stepFromUrl = new URLSearchParams(location.search).get('step');

    if (stepFromUrl) {
      // @ts-ignore -- legacy directive, needs investigation
      setStep(stepFromUrl);
    }
  }, [location.search]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const trackBuilderStep = () => {
    switch (step) {
      case 0:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'started',
        });
      case 1:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'profile',
        });
      case 2:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'services',
        });
      case 3:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'media',
        });
      case 4:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'spaces',
        });
      case 5:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'rooms',
        });
      case 6:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'experiences',
        });
      case 7:
        return amplitudeTrack('Complete Hotel Builder', {
          stepCompleted: 'finance',
        });
      default:
        return null;
    }
  };

  const prev = () => {
    setStep((currentStep) => (currentStep - 1) as StepNumber);
  };

  const next = () => {
    if (!validSteps[step]) {
      trackBuilderStep();
    }

    setValidSteps({ ...validSteps, [step]: true });

    setStep((currentStep) => (currentStep + 1) as StepNumber);
  };

  const handleSaveHotel = async (newValues: Partial<Hotel>) => {
    const guestContact = { ...hotel?.guestContact, ...newValues.guestContact };
    const newForm = {
      ...hotel,
      ...newValues,
      guestContact,
      channelManager:
        hotel?.channelManager === ChannelManager.NONE
          ? undefined
          : hotel?.channelManager,
    } as Hotel;

    const emails = newForm.notifEmails.map(({ email }) => email);
    const hasDuplicateEmails = new Set(emails).size !== emails.length;

    if (hasDuplicateEmails) {
      message.error('Duplicate emails not allowed');

      return;
    }

    await saveHotel(newForm);

    queryClient.invalidateQueries([hotelPath, { hotelId }]);

    next();
  };

  const handleSaveContacts = async (values: ExtranetHotelContacts) => {
    await saveContacts(values);

    next();
  };

  const renderForm = (hotelDefined: Hotel) => {
    switch (step) {
      case 0:
        return <Welcome handleNext={next} isLoading={false} />;
      case 1:
        return (
          <ProfileForm
            onFinish={handleSaveContacts}
            isLoading={isSavingContacts}
          />
        );
      case 2:
        return (
          <ServicesForm
            handleSubmit={(services) => {
              saveServices(services, {
                onSuccess: () =>
                  queryClient.invalidateQueries([hotelPath, { hotelId }]),
              });
              next();
            }}
            initialValue={hotelDefined.services.services || []}
            isLoading={false}
          />
        );
      case 3:
        return (
          <MediaForm
            initialValue={pictures}
            handleSubmit={(newPictures) => {
              saveGallery({
                id: hotelId,
                name: 'unused',
                pictures: newPictures,
                kind: 'official',
              });
              next();
            }}
            isLoading={false}
          />
        );
      case 4:
        return (
          <SpacesForm
            spaceTypes={spaceTypes}
            hotel={hotelDefined}
            onSubmit={next}
            isLoading={false}
          />
        );
      case 5:
        return rooms ? (
          <RoomsForm
            rooms={rooms}
            hotel={hotelDefined}
            saveRoom={(room) => saveHotelRooms(room)}
            deleteRoom={async (roomId) => await deleteRoom(roomId)}
            saveHotel={handleSaveHotel}
            isLoading={isSavingRooms || isSavingHotel}
          />
        ) : (
          <></>
        );
      case 6:
        return (
          <ExperiencesForm
            saveExperience={(experience) => saveHotelExperience(experience)}
            handleNext={next}
            hotelId={hotelId}
            isLoading={false}
          />
        );
      case 7:
        return (
          <FinancialForm
            initialValues={hotelDefined}
            handleSubmit={() => {
              setStep((currentStep) => (currentStep + 1) as StepNumber);
            }}
            countryId={hotel?.address.countryId}
            hotelId={hotelId}
            isLoading={false}
          />
        );
      case 8:
        return (
          <ThankYou
            handleNext={() => {
              /* no-op */
            }}
            showButton={isAdmin}
            isLoading={false}
          />
        );
      default:
        return null;
    }
  };

  if (!hotel || hotel.id !== hotelId) {
    return null;
  }

  if (!hotel.isLegacy) {
    return (
      <Layout>
        <SideMenu steps={[]} step={step} validSteps={validSteps} />
        <div className="hotel-builder">
          {isAdmin && (
            <div className="hotel-builder-error">
              Builder temporarily not accessible for NDM hotels
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SideMenu steps={steps} step={step} validSteps={validSteps} />
      <div className="hotel-builder">
        <CurrencyWrapper hotel={hotel}>
          <HeaderContext.Provider value={{ steps, step, prev }}>
            {renderForm(hotel as Hotel)}
          </HeaderContext.Provider>
        </CurrencyWrapper>
      </div>
    </Layout>
  );
};
